import { HttpErrorResponse } from "@angular/common/http";
import { Component, Input, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { IntentService } from "@tylertech/intents-module";
import { Utilities } from "@tylertech/shared-module";
import { ExecutionResult, IChartCriteria, ProfileSettingViewModel, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "utilities-outstanding-service-orders-chart",
    templateUrl: "./outstandingServiceOrdersChart.tmpl.html",
    standalone: false
})
export class OutstandingServiceOrdersChartComponent
    implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly destructor$ = new Subject<void>();
    readonly commands: ReadonlyArray<Command>;
    private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"pie">;

    private _chartCriteria: IChartCriteria;
    readonly customDataBinding: ChartBindingObject;
    ChartTitle: string = "Outstanding Service Orders";
    isLoaded: boolean;
    constructor(
        $chartManagerService: ChartManagerService,
        private readonly $chartRegistryService: ChartRegistryService,
        private readonly $chartDialogService: ChartDialogService,
        private readonly $chartService: ChartService,
        private readonly $loggingService: LoggingService,
        private readonly $fileService: FileService,
        private readonly $intentService: IntentService
    )
    {
        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.chartHasData,
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.customDataBinding?.chartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            } catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = true;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    } catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "pie",
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "right",
                        labels: { boxWidth: 12, color: "#333333" },
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"pie">): string | string[] =>
                            {
                                const label = tooltipItem.label;
                                const value = tooltipItem.raw;
                                return `${label}: ${value}`;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                },
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (element)
                    {
                        this.openServiceOrderSearch(element.index);
                    }
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        type: "pie",
                        data: this.chartCriteria.ChartDatas[0].Data,
                        backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length),
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }

    openSettings(): void
    {
        this.$chartDialogService
            .show(this.chartCriteria.Parameters, "Prepaid Billing")
            .then((result: ProfileSettingViewModel[]) =>
            {
                if (!Utilities.arraysEqual(this.chartCriteria.Parameters, result))
                {
                    this.chartCriteria.Parameters = result;
                    return this.loadChartData();
                }
            });
    }

    openServiceOrderSearch(index: number): void
    {
        if (index === undefined || index === null) return;
        const serviceOrder = this.chartCriteria.ChartDatas[0].Labels[index];
        new Promise((resolve, reject) =>
        {
            let result: ExecutionResult<string>;
            try
            {
                this.$intentService
                    .resolve("OpenServiceOrderSearch", "Open", { id: serviceOrder })
                    .then(responseJson =>
                    {
                        if (typeof responseJson === "string")
                        {
                            result = JSON.parse(responseJson);
                            if (result.Success) resolve(result);
                            else reject(result);
                        } else
                        {
                            resolve(<ExecutionResult<string>>{ Success: true });
                        }
                    });
            } catch (error)
            {
                result = <ExecutionResult<string>>{ Success: false };
                reject(result);
            }
        });
    }
}
